import React, { useState, useEffect } from 'react';
import {
  Navigation,
  Pencil,
  Crosshair,
  Sprout,
  LandPlot,
  ShoppingBag,
  Download,
  Share2,
  FileSpreadsheet,
  X,
} from 'lucide-react';
import type { Field, Product, Estate, Page, Plan } from '../../types';
import type { CustomerWorkspace } from '../../lib/customer-workspace';
import type { EstateDashboardMode } from '../workspace/EstateDashboardModeSwitcher';
import { MobileTopBar } from './MobileTopBar';
import { MobileBottomNav, type MobileTab } from './MobileBottomNav';
import { MobileInspectorSheet } from './MobileInspectorSheet';
import { MobileFieldsSheet } from './MobileFieldsSheet';
import { fieldAreaHa, getAreaTypeDefinition } from '../../lib/domain';
import { isJagtPoiField } from '../../lib/poi';
import { ErrorBoundary } from '../ErrorBoundary';
import { downloadDxf, downloadOrderCsv, downloadSurveyorCsv } from '../../lib/export';
import './mobile.css';
import './mobile-pages.css';
import { useDialogA11y } from '../../lib/useDialogA11y';

// Lazy import FieldMap if needed, or pass rendered
export interface MobileAppShellProps {
  page: Page;
  onNavigate: (page: Page) => void;
  estate?: Estate;
  estates: Estate[];
  selectedEstateId: string;
  onSelectEstate: (estateId: string) => void;
  customerWorkspace: CustomerWorkspace | null;
  onLeaveCustomer?: () => void;
  isAdmin?: boolean;
  isSemiPro?: boolean;
  isProView?: boolean;
  fields: Field[];
  products: Product[];
  planSelections: Record<string, string>;
  onSelectProduct: (fieldId: string, productId: string) => void;
  selectedFieldId: string;
  onSelectField: (fieldId: string) => void;
  onUpdateField: (fieldId: string, updates: Partial<Field>) => void;
  onDeleteField: (fieldId: string) => void;
  onOpenCart: () => void;
  cartCount: number;
  onOpenMenu: () => void;
  menuOpen: boolean;
  triggerGpsModal?: boolean;
  onGpsModalHandled?: () => void;
  triggerDraw?: any;
  onDrawTriggered?: () => void;
  onTriggerDraw: (kind?: 'vildtager' | 'zone' | 'line') => void;
  onTriggerGps: () => void;
  notify?: (message: string) => void;
  planReadOnly?: boolean;
  onToggleEditShape?: (editing: boolean) => void;
  estateDashboardMode?: EstateDashboardMode;
  onSelectDashboardMode?: (mode: EstateDashboardMode) => void;
  allowEstateCreation?: boolean;
  onCreateEstate?: () => void;
  onOpenPwaInstall?: () => void;
  hasAppUpdate?: boolean;
  onApplyAppUpdate?: () => void;
  lastUpdatedLabel?: string;
  /** Senest slettede mark, som kan gendannes fra Marker-arket. */
  deletedFieldName?: string;
  onRestoreDeleted?: () => void;
  /** Gem-/synkroniseringsstatus ('Gemt i kontoen' osv.) – samme tekst som desktopens topbar. */
  syncStatus?: string;
  /** Fejl-/konfliktbannere for lokal lagring og kontosynk (samme som desktopens). */
  syncBanners?: React.ReactNode;
  isGuest?: boolean;
  onOpenSavePlan?: () => void;
  renderMap: () => React.ReactNode;
  renderPageContent: () => React.ReactNode;
}

export const MobileAppShell: React.FC<MobileAppShellProps> = ({
  isGuest,
  onOpenSavePlan,
  page,
  onNavigate,
  estate,
  estates,
  selectedEstateId,
  onSelectEstate,
  customerWorkspace,
  onLeaveCustomer,
  isAdmin,
  isSemiPro,
  isProView,
  fields,
  products,
  planSelections,
  onSelectProduct,
  selectedFieldId,
  onSelectField,
  onUpdateField,
  onDeleteField,
  onOpenCart,
  cartCount,
  onOpenMenu,
  menuOpen,
  onTriggerDraw,
  onTriggerGps,
  notify,
  onToggleEditShape,
  estateDashboardMode,
  allowEstateCreation,
  onCreateEstate,
  onOpenPwaInstall,
  hasAppUpdate,
  onApplyAppUpdate,
  lastUpdatedLabel,
  deletedFieldName,
  onRestoreDeleted,
  syncStatus,
  syncBanners,
  renderMap,
  renderPageContent,
}) => {
  const [fieldsSheetOpen, setFieldsSheetOpen] = useState(false);
  const [poiSheetOpen, setPoiSheetOpen] = useState(false);
  const [poiPopoverOpen, setPoiPopoverOpen] = useState(false);
  const [naturePopoverOpen, setNaturePopoverOpen] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);

  const { ref: poiSheetRef } = useDialogA11y({
    open: poiSheetOpen,
    onClose: () => setPoiSheetOpen(false),
  });
  const { ref: exportSheetRef } = useDialogA11y({
    open: exportMenuOpen,
    onClose: () => setExportMenuOpen(false),
  });

  const selectedField = fields.find((f) => f.id === selectedFieldId) || null;

  // Determine active mobile tab based on page and modal states
  const activeTab: MobileTab =
    page === 'map' && !fieldsSheetOpen ? 'map' : fieldsSheetOpen ? 'fields' : 'map';

  const handleTabChange = (tab: MobileTab) => {
    if (tab === 'map') {
      setFieldsSheetOpen(false);
      if (page !== 'map') onNavigate('map');
    } else if (tab === 'fields') {
      if (page !== 'map') onNavigate('map');
      setFieldsSheetOpen(true);
    } else if (tab === 'cart') {
      onOpenCart();
    } else if (tab === 'menu') {
      onOpenMenu();
    }
  };

  // Close popovers on escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setPoiPopoverOpen(false);
        setNaturePopoverOpen(false);
        setExportMenuOpen(false);
        setFieldsSheetOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className="mobile-app-shell main-workspace">
      {/* 1. Mobile Top Bar */}
      <MobileTopBar
        estate={estate}
        estates={estates}
        selectedEstateId={selectedEstateId}
        onSelectEstate={onSelectEstate}
        customerWorkspace={customerWorkspace}
        onLeaveCustomer={onLeaveCustomer}
        isAdmin={isAdmin}
        cartCount={cartCount}
        onOpenCart={onOpenCart}
        onOpenMenu={onOpenMenu}
        menuOpen={menuOpen}
        page={page}
        onNavigate={onNavigate}
        allowEstateCreation={allowEstateCreation}
        onCreateEstate={onCreateEstate}
        onOpenPwaInstall={onOpenPwaInstall}
        onOpenExportSheet={
          (customerWorkspace || isProView || isAdmin) && estate
            ? () => setExportMenuOpen(true)
            : undefined
        }
        hasAppUpdate={hasAppUpdate}
        onApplyAppUpdate={onApplyAppUpdate}
        lastUpdatedLabel={lastUpdatedLabel}
        isGuest={isGuest}
        fieldCount={fields.length}
        onOpenSavePlan={onOpenSavePlan}
      />

      {syncStatus && (
        <div className="mobile-sync-status" role="status" data-testid="mobile-sync-status">
          <span aria-hidden="true" />
          {syncStatus}
        </div>
      )}
      {syncBanners && <div className="mobile-sync-banners">{syncBanners}</div>}

      {/* 2. Main Stage: Map or Page Content */}
      <div className="mobile-stage-container">
        {page === 'map' ? (
          <div className="mobile-map-stage">
            <ErrorBoundary fallbackTitle="Kortet kunne ikke indlæses">{renderMap()}</ErrorBoundary>
          </div>
        ) : (
          <div className="mobile-page-scroll-container">
            <ErrorBoundary fallbackTitle="Siden kunne ikke indlæses">
              {renderPageContent()}
            </ErrorBoundary>
          </div>
        )}
      </div>

      {/* 3. Mobile Inspector Bottom Sheet (Step 1-2-3 flow) */}
      {selectedField && (
        <MobileInspectorSheet
          field={selectedField}
          estate={
            estate || {
              id: '',
              name: 'Revir',
              address: '',
              center: [11.82, 55.4],
              createdAt: '',
            }
          }
          products={products}
          planSelections={planSelections}
          onSelectProduct={(fieldId, prodId) => {
            onSelectProduct(fieldId, prodId);
            notify?.('Frøblanding gemt.');
          }}
          onUpdateField={onUpdateField}
          onDeleteField={onDeleteField}
          onClose={() => onSelectField('')}
          onOpenCart={onOpenCart}
          onStartDrawing={onTriggerDraw}
          onEditShape={(fieldId) => {
            onSelectField(fieldId);
            onToggleEditShape?.(true);
            notify?.('Facon-redigering aktiveret. Træk i hjørnerne på kortet.');
          }}
          isSemiPro={isSemiPro}
          isProView={isProView}
          notify={notify}
        />
      )}

      {/* 3.5. Mobile Jagt-POI Quick Sheet */}
      {poiSheetOpen && (
        <>
          <div
            className="mobile-inspector-backdrop"
            onClick={() => setPoiSheetOpen(false)}
            aria-hidden="true"
          />
          <div
            ref={poiSheetRef}
            className="mobile-fields-sheet poi-sheet"
            role="dialog"
            aria-modal="true"
            aria-label="Placer Jagt-POI"
          >
            <div className="mobile-fields-list-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Crosshair size={18} color="#38bdf8" />
                <strong style={{ fontSize: '0.95rem', color: '#fff' }}>Placer Jagt-POI</strong>
              </div>
              <button
                type="button"
                className="mobile-sheet-close-btn"
                onClick={() => setPoiSheetOpen(false)}
                aria-label="Luk Jagt-POI menu"
              >
                <X size={18} />
              </button>
            </div>
            <div className="mobile-fields-list-scroll">
              {[
                {
                  type: 'skydetaarn',
                  label: 'Skydetårn / Hochsitz',
                  desc: 'Placering af skydetårn med skudretning',
                  color: '#b45309',
                },
                {
                  type: 'fodertoende',
                  label: 'Foderplads / Fodertønde',
                  desc: 'Foderautomat eller fodertønde',
                  color: '#0369a1',
                },
                {
                  type: 'vildtkamera',
                  label: 'Vildtkamera',
                  desc: 'Trailcam overvågning',
                  color: '#15803d',
                },
                {
                  type: 'saltsten',
                  label: 'Saltsten / Sliksten',
                  desc: 'Mineralsten til klovvildt',
                  color: '#ea580c',
                },
                {
                  type: 'jordmaaler',
                  label: 'Jordmåler / Sensor',
                  desc: 'Fugt- & temperaturmåler',
                  color: '#0d9488',
                },
                {
                  type: 'andet_punkt',
                  label: 'Andet jagt-punkt',
                  desc: 'Fast post, sti eller referencepunkt',
                  color: '#64748b',
                },
              ].map((item) => (
                <button
                  key={item.type}
                  type="button"
                  className="mobile-field-item-card"
                  onClick={() => {
                    setPoiSheetOpen(false);
                    notify?.(`Tryk på kortet for at placere ${item.label}.`);
                    const poiBtn = document.querySelector(
                      '.map-actionbar-poi-btn',
                    ) as HTMLButtonElement | null;
                    if (poiBtn) {
                      poiBtn.click();
                      setTimeout(() => {
                        const popoverItems = document.querySelectorAll('.poi-popover-item');
                        popoverItems.forEach((btn) => {
                          if (
                            btn.textContent?.includes(item.label) ||
                            (item.type === 'fodertoende' &&
                              btn.textContent?.includes('Foderplads')) ||
                            (item.type === 'skydetaarn' && btn.textContent?.includes('Skydetårn'))
                          ) {
                            (btn as HTMLButtonElement).click();
                          }
                        });
                      }, 50);
                    }
                  }}
                >
                  <div className="mobile-field-item-left">
                    <span
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: 8,
                        background: `color-mix(in srgb, ${item.color} 20%, transparent)`,
                        border: `1.5px solid ${item.color}`,
                        color: item.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.8rem',
                        fontWeight: 700,
                      }}
                    >
                      <Crosshair size={16} />
                    </span>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        textAlign: 'left',
                      }}
                    >
                      <strong style={{ fontSize: '0.88rem', color: '#fff' }}>{item.label}</strong>
                      <span style={{ fontSize: '0.74rem', color: '#94a3b8' }}>{item.desc}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* 4. Mobile Fields Full Sheet */}
      {fieldsSheetOpen && (
        <MobileFieldsSheet
          fields={fields}
          selectedFieldId={selectedFieldId}
          onSelectField={(id) => {
            onSelectField(id);
            setFieldsSheetOpen(false);
          }}
          onClose={() => setFieldsSheetOpen(false)}
          onTriggerDraw={() => {
            setFieldsSheetOpen(false);
            onTriggerDraw('vildtager');
          }}
          onTriggerGps={() => {
            setFieldsSheetOpen(false);
            onTriggerGps();
          }}
          deletedFieldName={deletedFieldName}
          onRestoreDeleted={onRestoreDeleted}
          onOpenExport={
            (customerWorkspace || isProView || isAdmin) && estate
              ? () => setExportMenuOpen(true)
              : undefined
          }
        />
      )}

      {/* 4.5. Mobile Export Sheet (DXF & CSV) */}
      {exportMenuOpen && estate && (
        <>
          <div
            className="mobile-inspector-backdrop"
            onClick={() => setExportMenuOpen(false)}
            aria-hidden="true"
          />
          <div
            ref={exportSheetRef}
            className="mobile-fields-sheet mobile-export-sheet"
            role="dialog"
            aria-modal="true"
            aria-label="Eksportér revirdata"
          >
            <div className="mobile-fields-list-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Download size={18} color="#4ade80" />
                <strong style={{ fontSize: '0.95rem', color: '#fff' }}>
                  Eksportér data & planer
                </strong>
              </div>
              <button
                type="button"
                className="mobile-sheet-close-btn"
                onClick={() => setExportMenuOpen(false)}
                aria-label="Luk eksportmenu"
              >
                <X size={18} />
              </button>
            </div>
            <div className="mobile-fields-list-scroll">
              <button
                type="button"
                className="mobile-field-item-card"
                onClick={() => {
                  try {
                    downloadDxf(fields, estate.name);
                    notify?.('CAD DXF fil genereret og downloadet.');
                    setExportMenuOpen(false);
                  } catch {
                    notify?.('Kunne ikke eksportere DXF.');
                  }
                }}
              >
                <div className="mobile-field-item-left">
                  <span
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      background: 'rgba(56, 189, 248, 0.15)',
                      border: '1.5px solid #38bdf8',
                      color: '#38bdf8',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.75rem',
                      fontWeight: 800,
                    }}
                  >
                    DXF
                  </span>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      textAlign: 'left',
                    }}
                  >
                    <strong style={{ fontSize: '0.88rem', color: '#fff' }}>
                      CAD DXF Fil (EPSG:25832)
                    </strong>
                    <span style={{ fontSize: '0.74rem', color: '#94a3b8' }}>
                      Markskel og zoner til GIS og traktor-GPS
                    </span>
                  </div>
                </div>
              </button>

              <button
                type="button"
                className="mobile-field-item-card"
                onClick={() => {
                  try {
                    downloadSurveyorCsv(fields, estate.name);
                    notify?.('Landmåler CSV fil downloadet.');
                    setExportMenuOpen(false);
                  } catch {
                    notify?.('Kunne ikke eksportere CSV.');
                  }
                }}
              >
                <div className="mobile-field-item-left">
                  <span
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      background: 'rgba(34, 197, 94, 0.15)',
                      border: '1.5px solid #22c55e',
                      color: '#4ade80',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.75rem',
                      fontWeight: 800,
                    }}
                  >
                    CSV
                  </span>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      textAlign: 'left',
                    }}
                  >
                    <strong style={{ fontSize: '0.88rem', color: '#fff' }}>
                      Landmåler CSV (WGS84)
                    </strong>
                    <span style={{ fontSize: '0.74rem', color: '#94a3b8' }}>
                      Præcise GPS-koordinater til rover og Excel
                    </span>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </>
      )}

      {/* 5. Mobile Bottom Navigation Bar */}
      <MobileBottomNav
        activeTab={activeTab}
        onTabChange={handleTabChange}
        fieldsCount={fields.length}
        cartCount={cartCount}
        onOpenMenu={onOpenMenu}
      />
    </div>
  );
};
